import React from "react";
import { Box, Card, Stack, Typography, Button } from "@mui/material";
import {
  HomeRounded,
  CancelOutlined,
  TaskAltRounded,
  StorefrontRounded,
  LogoutOutlined,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ROUTES,
  MESSAGES,
  version,
  primaryDark,
  accentColor,
  supporterBgGradient,
  myApi, // ← ekle
} from "../../../constants/consts";

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-24px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const NAV = [
  {
    key: "home",
    label: MESSAGES.SUPPORTER_SIDEBAR_HOME,
    icon: HomeRounded,
    path: ROUTES.SUPPORTER_HOME,
  },
  {
    key: "cancelled",
    label: MESSAGES.SUPPORTER_SIDEBAR_CANCELLED,
    icon: CancelOutlined,
    path: ROUTES.SUPPORTER_CANCELLED,
  },
  {
    key: "completed",
    label: MESSAGES.SUPPORTER_SIDEBAR_COMPLETED,
    icon: TaskAltRounded,
    path: ROUTES.SUPPORTER_COMPLETED,
  },
];

export default function SupporterLayout({ user, onLogout, children }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <Box
      sx={{
        height: "100dvh",
        display: "flex",
        overflow: "hidden",
        background: supporterBgGradient,
      }}
    >
      {/* SIDEBAR */}
      <Box
        sx={{
          width: { xs: 78, md: 240 },
          flexShrink: 0,
          p: { xs: 1, md: 2 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
          height: "100%", // ← sidebar tam yükseklik
          minHeight: 0,
          overflow: "hidden",
          animation: `${slideInLeft} 0.5s cubic-bezier(0.22, 1, 0.36, 1) both`,
        }}
      >
        <Card
          sx={{
            flex: 1,
            minHeight: 0, // ← Card içeriği taşmasın
            borderRadius: 3,
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            backgroundColor: "rgba(237, 240, 242, 0.98)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
            overflow: "hidden",
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 1,
              animation: `${fadeInUp} 0.4s ease-out 0.1s backwards`,
            }}
          >
            <Box
              component="img"
              src="/images/logo.png"
              alt="Nortek"
              sx={{
                height: { xs: 20, md: 28 },
                width: "auto",
                objectFit: "contain",
                filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.15))",
              }}
            />
          </Box>

          {/* Nav */}
          <Stack sx={{ flex: 1, gap: 0.75, mt: 1 }}>
            {NAV.map((item, i) => {
              const Icon = item.icon;
              const selected = pathname === item.path;
              return (
                <Box
                  key={item.key}
                  onClick={() => navigate(item.path)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    px: { xs: 1, md: 1.5 },
                    py: 1.25,
                    borderRadius: 2,
                    cursor: "pointer",
                    justifyContent: { xs: "center", md: "flex-start" },
                    color: selected ? "#fff" : "#334155",
                    background: selected
                      ? `linear-gradient(135deg, ${primaryDark} 0%, ${accentColor} 100%)`
                      : "transparent",
                    boxShadow: selected
                      ? "0 6px 16px rgba(4,87,129,0.30)"
                      : "none",
                    fontWeight: 700,
                    fontSize: 13.5,
                    transition:
                      "transform 0.22s cubic-bezier(0.22,1,0.36,1), background 0.22s ease, color 0.22s ease, box-shadow 0.22s ease",
                    animation: `${fadeInUp} 0.4s ease-out ${0.15 + i * 0.06}s backwards`,
                    "&:hover": {
                      transform: "translateX(3px)",
                      background: selected ? undefined : "rgba(4,87,129,0.07)",
                    },
                    "&:active": { transform: "translateX(1px) scale(0.98)" },
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                  <Box
                    component="span"
                    sx={{ display: { xs: "none", md: "inline" } }}
                  >
                    {item.label}
                  </Box>
                </Box>
              );
            })}
          </Stack>

          {/* Store + logout + version */}
          <Stack spacing={1} sx={{ mt: "auto" }}>
            <Button
              fullWidth
              onClick={() => window.open(myApi, "_blank")}
              startIcon={<StorefrontRounded />}
              sx={{
                justifyContent: { xs: "center", md: "flex-start" },
                textTransform: "none",
                fontWeight: 700,
                color: primaryDark,
                borderRadius: 2,
                px: 1.5,
                animation: `${fadeInUp} 0.4s ease-out 0.4s backwards`,
                "& .MuiButton-startIcon": { mr: { xs: 0, md: 1 } },
                "& .MuiButton-endIcon": { ml: "auto" },
                "& span.MuiButton-label": {
                  display: { xs: "none", md: "inline" },
                },
                "&:hover": { backgroundColor: "rgba(4,87,129,0.08)" },
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", md: "inline" } }}
              >
                {MESSAGES.SUPPORTER_STORE}
              </Box>
            </Button>

            <Button
              fullWidth
              onClick={onLogout}
              startIcon={<LogoutOutlined />}
              sx={{
                justifyContent: { xs: "center", md: "flex-start" },
                textTransform: "none",
                fontWeight: 700,
                color: "#B91C1C",
                borderRadius: 2,
                px: 1.5,
                animation: `${fadeInUp} 0.4s ease-out 0.46s backwards`,
                "& .MuiButton-startIcon": { mr: { xs: 0, md: 1 } },
                "&:hover": { backgroundColor: "rgba(185,28,28,0.08)" },
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", md: "inline" } }}
              >
                Çıkış
              </Box>
            </Button>

            <Typography
              variant="caption"
              sx={{
                textAlign: "center",
                color: "#94A3B8",
                fontWeight: 700,
                animation: `${fadeInUp} 0.4s ease-out 0.5s backwards`,
              }}
            >
              {version}
            </Typography>
          </Stack>
        </Card>
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1, // genişlik için (row flex'te)
          minWidth: 0,
          height: "100%", // kesin yükseklik
          display: "flex",
          flexDirection: "column",
          p: { xs: 1.5, md: 2 },
          boxSizing: "border-box",
          overflow: "hidden",
          animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
